import React, { useState, useEffect } from "react";
import AdminSidebar from "../../components/AdminSidebar";
import {
  Database,
  Search,
  Trash2,
  Eye,
  Edit,
  Rss,
  Star,
  Menu,
} from "lucide-react";
import {
  fetchCustomModels,
  updateModelStatus,
  deleteCustomModel,
} from "../../../services/modelApi";
import { toast } from "react-toastify";
import { ConfirmationModal } from "../../components/ConfirmationModel";
import { useNavigate } from "react-router-dom";
import { fetchAllFeedbacksApi } from "../../../services/feedbackApi";
import { Drawer } from "../../components/ui/Drawer";

const FeedbackListing = () => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [updatingModel, setUpdatingModel] = useState(null);
  const navigate = useNavigate();

  const [feedbacks, setFeedbacks] = useState([]);
  useEffect(() => {
    fetchData();
  }, []);
  const fetchData = async () => {
    setLoading(true);
    try {
      const [feedbacksRes] = await Promise.all([fetchAllFeedbacksApi()]);

      // Models are directly in response.data, just like in CentreArea
      setFeedbacks(feedbacksRes.data.feedbacks || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modelToDelete, setModelToDelete] = useState(null);

  useEffect(() => {
    fetchModels();
  }, []);

  const fetchModels = async () => {
    setLoading(true);
    try {
      const response = await fetchCustomModels();
      setModels(response.data);
    } catch (error) {
      console.error("Error fetching models:", error);
      toast.error("Failed to fetch models");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (modelId, newStatus) => {
    setUpdatingModel(modelId);
    try {
      await updateModelStatus(modelId, newStatus);
      toast.success("Model status updated successfully");
      fetchModels(); // Refresh the list
    } catch (error) {
      console.error("Error updating model status:", error);
      toast.error(error.message || "Failed to update model status");
    } finally {
      setUpdatingModel(null);
    }
  };

  // Open delete confirmation modal
  const handleDeleteClick = (model) => {
    setModelToDelete(model);
    setShowDeleteModal(true);
  };

  // Handle confirmed deletion
  const handleConfirmDelete = async () => {
    if (!modelToDelete) return;

    setUpdatingModel(modelToDelete._id);
    setShowDeleteModal(false);

    try {
      setModels((prevModels) =>
        prevModels.filter((model) => model._id !== modelToDelete._id)
      );
      await deleteCustomModel(modelToDelete._id);
      toast.success("Model deleted successfully");
    } catch (error) {
      console.error("Error deleting model:", error);
      toast.error(error.message || "Failed to delete model");
    } finally {
      setUpdatingModel(null);
      setModelToDelete(null);
    }
  };

  // Close modal
  const handleCloseModal = () => {
    setShowDeleteModal(false);
    setModelToDelete(null);
  };

  // Handle edit model
  const handleEdit = (model) => {
    navigate(`/admin-dashboard/model-edit/${model._id}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "bg-green-500/20 text-green-400";
      case "inactive":
        return "bg-gray-500/20 text-gray-400";
      default:
        return "bg-gray-500/20 text-gray-400";
    }
  };

  const getStatusDot = (status) => {
    switch (status) {
      case "active":
        return "bg-green-400";
      case "inactive":
        return "bg-gray-500";
      default:
        return "bg-gray-500";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const filteredModels = models.filter(
    (model) =>
      model.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      model.modelHandle?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const [showSidebar, setShowSidebar] = useState(false);
  const onClose = () => {
    setShowSidebar(false);
  };

  return (
    <div className="w-screen min-h-screen bg-gray-800 overflow-x-hidden">
      <div className="flex min-h-screen">
        {/* Sidebar */}
        <div className="hidden lg:block w-64 h-full">
          <AdminSidebar />
        </div>
        <Drawer
          height="full"
          title={"Admin"}
          isOpen={showSidebar}
          onClose={onClose}
        >
          <div className=" lg:hidden  w-full h-full">
            <AdminSidebar />
          </div>
        </Drawer>
        {/* Main Content */}
        <div className="flex-1 flex flex-col h-full overflow-y-auto bg-gray-800">
          {/* Header */}
          <div className="px-8 py-6 items-start justify-between flex border-b border-gray-700">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-3 mb-2 md:mb-0">
                <Rss className="text-blue-400" size={24} />
                <h1 className="text-2xl font-semibold text-white">
                  User Feedbacks
                </h1>
              </div>
            </div>
            <div
              onClick={() => {
                setShowSidebar(true);
              }}
              className="max-w-max lg:hidden"
            >
              <Menu size={30} color="white" />
            </div>
          </div>

          {/* Table Content */}
          <div className="flex-1 p-3 lg:p-8">
            <div className="bg-gray-900 border border-gray-700 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-700">
                  <thead className="bg-gray-800">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                        User Name
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                        Model
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                        Rating
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                        Feedback Type
                      </th>

                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                        Feedback Message
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-gray-900 divide-y divide-gray-700">
                    {loading ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-12 text-center">
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
                            <span className="ml-2 text-gray-400">
                              Loading models...
                            </span>
                          </div>
                        </td>
                      </tr>
                    ) : filteredModels.length === 0 ? (
                      <tr>
                        <td
                          colSpan="5"
                          className="px-6 py-12 text-center text-gray-400"
                        >
                          {searchTerm
                            ? "No models found matching your search"
                            : "No models found"}
                        </td>
                      </tr>
                    ) : (
                      feedbacks.map((model) => (
                        <tr
                          key={model._id}
                          className="hover:bg-gray-800 transition-colors"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div
                                className={`w-2 h-2 rounded-full ${getStatusDot(
                                  model.status || "active"
                                )} mr-3`}
                              ></div>
                              <span className="font-medium capitalize text-white text-sm">
                                {model.user.username}{" "}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-300 text-sm font-mono">
                            {model.modelHandle || "N/A"}
                          </td>
                          <td className="px-6 flex items-center gap-1 py-4 whitespace-nowrap text-gray-300 text-sm">
                            {model.rating}
                            <Star size={17} fill="yellow" stroke="0px" />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-300 text-sm">
                            {model.feedbackType}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-300 text-sm">
                            <div title={model.feedback} className="max-w-max">
                              {model.feedback.substring(0, 20)}
                              {model.feedback.length > 20 && "..."}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDelete}
        title="Delete Model"
        message={`Are you sure you want to delete model "${modelToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isDestructive={true}
      />
    </div>
  );
};

export default FeedbackListing;
